import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase, db } from '../lib/supabase';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ── Device fingerprint (browser/OS info for new-login alerts) ─────────────────
function getDeviceFingerprint() {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  let os = 'Unknown OS';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  let browser = 'Unknown Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\//.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  return { os, browser, label: `${browser} on ${os}` };
}

// ── Known devices (stored in localStorage per user) ──────────────────────────
const KNOWN_DEVICES_KEY = 'aspreo_known_devices';
function getKnownDevices(uid) {
  try { return JSON.parse(localStorage.getItem(`${KNOWN_DEVICES_KEY}_${uid}`) || '[]'); } catch { return []; }
}
function markDeviceKnown(uid, fingerprint) {
  const known = getKnownDevices(uid);
  if (!known.includes(fingerprint)) {
    known.push(fingerprint);
    try { localStorage.setItem(`${KNOWN_DEVICES_KEY}_${uid}`, JSON.stringify(known.slice(-10))); } catch {}
  }
}
function isKnownDevice(uid, fingerprint) {
  return getKnownDevices(uid).includes(fingerprint);
}

export function AuthProvider({ children }) {
  const [user,          setUser]          = useState(null);
  const [profile,       setProfile]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [newLoginAlert, setNewLoginAlert] = useState(null);
  const loginCheckedRef = useRef(false);

  const loadProfile = useCallback(async (uid) => {
    let { data } = await db.getProfile(uid);
    if (!data) {
      // Defense-in-depth retry for any transient failure on the first fetch
      // right after sign-in (network blip, etc).
      await new Promise(r => setTimeout(r, 600));
      ({ data } = await db.getProfile(uid));
    }
    if (data) setProfile(data);
    return data;
  }, []);

  const loadNotifications = useCallback(async (uid) => {
    const { data } = await db.getNotifications(uid);
    if (data) setNotifications(data);
  }, []);

  const refreshProfile = useCallback(() => {
    if (user) loadProfile(user.id);
  }, [user, loadProfile]);

  const checkNewDevice = useCallback((uid) => {
    if (loginCheckedRef.current) return;
    loginCheckedRef.current = true;
    const fp = getDeviceFingerprint();
    const known = isKnownDevice(uid, fp.label);
    if (!known) {
      setNewLoginAlert({ device: fp.label, time: new Date().toLocaleTimeString() });
      markDeviceKnown(uid, fp.label);
      setTimeout(() => setNewLoginAlert(null), 10000);
    } else {
      markDeviceKnown(uid, fp.label);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        loadProfile(u.id);
        loadNotifications(u.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        loadProfile(u.id);
        loadNotifications(u.id);
        if (event === 'SIGNED_IN' && !loginCheckedRef.current) {
          loginCheckedRef.current = false;
          checkNewDevice(u.id);
        }
      } else {
        setProfile(null);
        setNotifications([]);
        loginCheckedRef.current = false;
      }
    });

    return () => subscription.unsubscribe();
  }, [loadProfile, loadNotifications, checkNewDevice]);

  // Realtime notifications intentionally disabled
  // WebSocket (wss://) cannot be proxied through Vercel rewrites
  // All notifications load on page load via loadNotifications() above

  const markNotifRead = async (id) => {
    await db.markNotifRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = async () => {
    if (!user) return;
    await db.markAllNotifRead(user.id);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      notifications, unreadCount, markNotifRead, markAllRead,
      refreshProfile,
      newLoginAlert, dismissNewLoginAlert: () => setNewLoginAlert(null),
      isAdmin:   profile?.role === 'admin',
      isPremium: profile?.role === 'premium' || profile?.role === 'admin',
    }}>
      {children}
    </AuthContext.Provider>
  );
}
